/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/markdown/MarkdownSection.ts
 * @create:      2021-09-01 13:13:13.858
 * @modify:      2025-07-11 20:47:10.727
 * @version:     6.0.0
 * @times:       37
 * @lines:       197
 * @copyright:   Copyright © 2021-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { Strings_Empty } from "../const/Strings";
import { KavenStack } from "../class/KavenStack";
import { ReplaceAll } from "../string/ReplaceAll";

export class MarkdownSection {
    public Heading: string;

    public Lines: string[] = [];

    public HasChildFlag = false;

    public PreLines: string[] = [];
    public Sections: MarkdownSection[] = [];

    public constructor(heading = Strings_Empty) {
        this.Heading = heading;
    }

    public get IsTop() {
        return this.Heading === Strings_Empty;
    }

    public get HasChild() {
        return this.Sections.length > 0;
    }

    public toString() {
        return this.Heading;
    }

    public Sort(compareFn?: (a: MarkdownSection, b: MarkdownSection) => number) {
        const stack = new KavenStack<MarkdownSection>();
        stack.Push(this);

        do {
            const c = stack.Pop()!;

            for (const s of c.Sections) {
                stack.Push(s);
            }

            c.Sections.sort(compareFn);
        } while (stack.Count > 0);

        return this;
    }

    public GetLines(): string[] {
        let lines: string[];
        if (this.HasChild) {
            lines = this.PreLines.concat(...this.Sections.flatMap(p => p.GetLines()));
        } else {
            lines = this.PreLines.concat(this.Lines);
        }

        if (this.IsTop) {
            return lines;
        }

        return [this.Heading].concat(lines);
    }

    public static IsHeading(line: string) {
        return line.startsWith("#");
    }

    public static GetHeadingLevel(line: string) {
        const p1 = line.split(" ")[0];
        if (ReplaceAll(p1, "#", "").length === 0) {
            return p1.length;
        }

        return 0;
    }

    public static ParseSections(ms: MarkdownSection): MarkdownSection {
        const stack = new KavenStack<MarkdownSection>();
        stack.Push(ms);

        do {
            const current = stack.Pop()!;

            let headerLevel = 0;
            let section: MarkdownSection | undefined;

            let isFencedCodeBlocks = false;

            for (const line of current.Lines) {
                const level = this.GetHeadingLevel(line);

                let isFCB = line.startsWith("```");
                if (!isFencedCodeBlocks && isFCB) {
                    isFencedCodeBlocks = true;
                    isFCB = false;
                }

                if (isFencedCodeBlocks) {
                    if (section === undefined) {
                        current.PreLines.push(line);
                    } else {
                        section.Lines.push(line);
                    }

                    if (isFCB) {
                        isFencedCodeBlocks = false;
                    }

                    continue;
                }

                if (level > 0) {
                    // headings
                    if (headerLevel === 0) {
                        headerLevel = level;
                        section = new MarkdownSection(line);
                    } else if (level === headerLevel) {
                        /* istanbul ignore next */
                        if (section === undefined) {
                            throw new Error("section is undefined");
                        }

                        if (section.HasChildFlag) {
                            stack.Push(section);
                        }

                        current.Sections.push(section);
                        section = new MarkdownSection(line);
                    } else {
                        /* istanbul ignore next */
                        if (section === undefined) {
                            current.PreLines.push(line);
                        } else {
                            section.Lines.push(line);
                            section.HasChildFlag = true;
                        }
                    }
                } else {
                    if (section === undefined) {
                        current.PreLines.push(line);
                    } else {
                        section.Lines.push(line);
                    }
                }
            }

            if (section !== undefined) {
                if (section.HasChildFlag) {
                    stack.Push(section);
                }

                current.Sections.push(section);
            }
        } while (stack.Count > 0);

        // for convenient
        return ms;
    }

    public static Parse(lines: string[]): MarkdownSection {
        const ms = new MarkdownSection();
        ms.Lines = lines;
        return this.ParseSections(ms);
    }
}
